import { isObject } from 'lodash';
import { useEffect, useState } from 'react';

import { isBrowser } from '../utils';

const parseReadValue = (value: string) => {
  try {
    return JSON.parse(value);
  } catch (e) {
    console.warn(`Could not parse value read from localStorage.`);
  }
  return value;
};

const readValue = (key: string) => {
  if (isBrowser()) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      console.warn(`Could not read "${key}" from localStorage.`);
    }
  }
  return undefined;
};

const stringifyWriteValue = (value: any) => {
  try {
    return value && isObject(value) ? JSON.stringify(value) : value;
  } catch (e) {
    console.warn(
      `Could not stringify value before writing it to localStorage.`
    );
  }
  return value;
};

const writeValue = (key: string, value: string) => {
  if (isBrowser()) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      console.warn(`Could not write "${key}" to localStorage.`);
    }
  }
};

const useLocalStorage = (key: string) => {
  // Use `useState` to prevent `localStorage` from being
  // accessed on every rerun of this hook.
  const [value, setValue] = useState(() => {
    const rawValue = readValue(key);
    return rawValue ? parseReadValue(rawValue) : rawValue;
  });
  useEffect(() => {
    const rawValue = readValue(key);
    setValue(rawValue ? parseReadValue(rawValue) : rawValue);
  }, [key]);
  useEffect(() => {
    const stringifiedValue = stringifyWriteValue(value);
    writeValue(key, stringifiedValue);
  }, [key, value]);
  return [value, setValue];
};

export default useLocalStorage;
